import * as Sentry from '@sentry/browser'
import useAxios from 'axios-hooks'
import type { UseAxiosResult } from 'axios-hooks'
import type { AxiosRequestConfig } from 'axios'
import i18n from 'config/i18n'

import { fetchAPI } from 'libs/apis'
import type { WithIParam } from 'libs/apis'
import type {
  Branch,
  IBranchSingleProps,
  IBranchSinglePropsPartial,
} from './model'
import { LIMIT_PAGE } from 'helpers/constants/common'

import { BranchForApiSubmit, IResponseService, STATUS } from 'types'
import { IParamForSubmit, IParam } from 'libs/apis/helper'
import { IResponseBranch } from '.'

export const findAll = async (
  params: WithIParam<IBranchSinglePropsPartial> & {
    _limit?: string | number
    code?: string
  },
  options?: AxiosRequestConfig,
): Promise<{ body: IBranchSingleProps[]; totals: number }> => {
  const withDefaultParams: IParamForSubmit = {
    _limit: LIMIT_PAGE,
    isActive: params?.isActive || true,
    ...params,
    searchColumns: [
      'name',
      'code',
      'company.name',
      'contacts.number',
      'addresses.provinceTh',
      'addresses.districtTh',
      'addresses.subDistrictTh',
      'addresses.address',
    ],
  }

  if (withDefaultParams?.isActive === STATUS.ALL) {
    delete withDefaultParams.isActive
  }

  const newParams = {
    ...withDefaultParams,
    code: params.code?.trim(),
  }

  const branches = await fetchAPI({
    apiUrl: window._env_.REACT_APP_API_URL,
    method: 'GET',
    path: '/v1/branch',
    params: newParams,
    options,
  })

  if (branches.status !== 200) {
    throw new Error(i18n.t('error:notFound.title'))
  }

  return {
    body: branches.body as IBranchSingleProps[],
    totals: Number(branches.headers['x-total-count']),
  }
}

export const useFindAll = (
  params: WithIParam<IBranchSinglePropsPartial>,
): UseAxiosResult<IBranchSingleProps[]> => {
  const withLimitParams = {
    ...params,
    _limit: params._limit || LIMIT_PAGE,
  }

  const path = `${window._env_.REACT_APP_API_URL}/v1/branch`

  return useAxios({
    url: path,
    params: withLimitParams,
  })
}

export const findBranches = async (
  searchParams: Omit<IParam, '_page'> & { _page: number },
): Promise<IResponseService<IResponseBranch[]>> => {
  try {
    const branches = await fetchAPI<IResponseBranch[]>({
      apiUrl: window._env_.REACT_APP_API_URL,
      method: 'GET',
      path: `/v1/branch`,
      params: {
        ...searchParams,
        searchColumns: ['name', 'company.name', 'contacts.number'],
      },
    })

    return {
      data: branches.body,
      count: Number(branches.headers['x-total-count']),
    }
  } catch (error) {
    Sentry.captureException(error)

    return { error }
  }
}

export const findBranchById = async (
  id: string,
): Promise<IResponseService<Branch>> => {
  try {
    const response = await fetchAPI<Branch>({
      apiUrl: window._env_.REACT_APP_API_URL,
      method: 'GET',
      path: `/v1/branch/${id}`,
    })

    return { data: response.body, count: +response.headers['x-total-count'] }
  } catch (error) {
    Sentry.captureException(error)

    return { error }
  }
}

export const create = async (
  payload: BranchForApiSubmit,
  handleOpenSuccess: () => void,
): Promise<Branch> => {
  const response = await fetchAPI<Branch>({
    apiUrl: window._env_.REACT_APP_API_URL,
    method: 'POST',
    path: '/v1/branch',
    options: { data: payload },
  })

  const isSuccess = response.status === 200 || response.status === 201

  if (isSuccess) {
    handleOpenSuccess()
  } else {
    throw new Error(i18n.t('error:notFound.title'))
  }

  return response.body
}

export const update = async (
  id: string,
  payload: BranchForApiSubmit,
  handleOpenSuccess: () => void,
): Promise<Branch> => {
  const response = await fetchAPI<Branch>({
    apiUrl: window._env_.REACT_APP_API_URL,
    method: 'PATCH',
    path: `/v1/branch/${id}`,
    options: { data: payload },
  })

  const isSuccess = response.status === 200 || response.status === 201

  if (isSuccess) {
    handleOpenSuccess()
  } else {
    throw new Error(i18n.t('error:notFound.title'))
  }

  return response.body
}

export const useFindOneById = (id: string): UseAxiosResult<Branch> => {
  const path = `${window._env_.REACT_APP_API_URL}/v1/branch/${id}`

  return useAxios(path)
}

export const downloadBranchTemplate = async (): Promise<Blob> => {
  const response = await fetchAPI<{
    arrayBuffer(): Blob
  }>({
    apiUrl: window._env_.REACT_APP_API_URL,
    method: 'GET',
    path: '/v1.2/branch/import/template',
    responseType: 'blob',
  })

  return response.body.arrayBuffer()
}
